import React, { useEffect, useState, useCallback } from "react";
import i18next from "i18next";
import { Box } from "@mui/material";
import styles from "./report.module.scss";
import { Form, Formik } from "formik";
import FormikControl from "../../components/formComponents/FormikControl";
import Button from "../../components/buttons/Buttons";
import { useApiErrorModalContext } from "../../errorHandling/ApiErrorModalContext";
import * as Yup from "yup";
import {
  generalValidationMessage,
  dateInputValidation,
  dateFormats,
  downloadCsv,
} from "../../config/appConfig";
import _ from "lodash";
import {
  generateInvoice,
  postReportAPI,
} from "../../services/APIService";
import ReportTabel from "./ReportTable/ReportTable";

const Report = () => {
  const defaultValues = {
    provider: [],
    fromeDate: null,
    toDate: null,
    btnType: "",
  };
  const defaultPageSize = 20;
  const defaultPageNumber = 1;
  const generalParams = {
    pageNumber: defaultPageNumber,
    pageSize: defaultPageSize,
    isDesc: false,
  };
  const initalValue = defaultValues;
  const translatedGeneralValidationMessage = `${i18next.t(
    generalValidationMessage
  )}`;
  const { setApiErrorModalState } = useApiErrorModalContext();
  const [defaultAPIAdditionalParams, setDefaultAPIAdditionalParams] =
    useState(generalParams);
  const [apiParams, setApiParams] = useState({});
  const [sortModel, setSortModel] = useState([]);
  const [reportTable, setReportTable] = useState([]);
  const [reportResponse, setReportResponse] = useState({});

  const providerList = [
    { key: "RDC", value: "RDC" },
    { key: "VWE", value: "VWE" },
    { key: "ASP", value: "ASP" },
    { key: "MOX", value: "MOX" },
    { key: "PR5", value: "PR5" },
    { key: "PR6", value: "PR6" },
    { key: "Onbekend", value: "Onbekend" },
  ];

  const handleTableSorting = useCallback(
    (sortingParam) => {
      if (!_.isEmpty(sortingParam)) {
        let query = sortingParam[0];
        setDefaultAPIAdditionalParams({
          ...defaultAPIAdditionalParams,
          ...{
            isDesc: query?.sort === "desc",
            orderBy: query?.field,
          },
        });
        setSortModel([
          {
            field: query?.field,
            sort: query?.sort,
          },
        ]);
      } else {
        delete defaultAPIAdditionalParams["orderBy"];
        setDefaultAPIAdditionalParams({
          ...defaultAPIAdditionalParams,

          ...{
            isDesc: false,
          },
        });

        setSortModel([]);
      }
    },
    [defaultAPIAdditionalParams]
  );

  const validationSchema = Yup.object().shape({
    provider: Yup.array()
      .min(1, translatedGeneralValidationMessage)
      .typeError(translatedGeneralValidationMessage)
      .required(translatedGeneralValidationMessage),
    fromeDate: Yup.string()
      .typeError(translatedGeneralValidationMessage)
      .required(translatedGeneralValidationMessage),
    toDate: Yup.string()
      .typeError(translatedGeneralValidationMessage)
      .required(translatedGeneralValidationMessage),
    btnType: Yup.string(),
  });

  const handleClear = (setFieldValue, setFieldTouched) => {
    setReportTable([]);
    setReportResponse({});
    setApiParams({});
    setDefaultAPIAdditionalParams(generalParams);

    setFieldValue("provider", []);
    setFieldTouched("provider", false);

    setFieldValue("fromeDate", "");
    setFieldTouched("fromeDate", false);

    setFieldValue("toDate", "");
    setFieldTouched("toDate", false);
  };

  const fetchViewReport = (
    values,
    additionalParams = defaultAPIAdditionalParams
  ) => {
    setApiParams(values);

    postReportAPI({
      ...values,
      ...additionalParams,
    })
      .then((response) => {
        if (
          !_.isEmpty(_.get(response, "errorMessage", "")) ||
          _.get(response, "success", true) === false
        ) {
          _.set(response, "status", 200);

          setApiErrorModalState({ error: response });
        } else {
          setReportResponse(response);
          const reportList = _.get(response, "invoicingDtoList", []);
          const reportListWithId = reportList.map((row, index) => ({
            ...row,
            id: `${row.exact_Number}-${row.item_Code}-${index}`,
          }));
          setReportTable(reportListWithId);
        }
      })
      .catch((err) => {
        setApiErrorModalState({ error: err });
      });
  };
  useEffect(() => {
    if (!_.isEmpty(apiParams)) {
      fetchViewReport(apiParams, defaultAPIAdditionalParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAPIAdditionalParams]);

  const getApiFormattedValue = (values) => {
    let providerId = values["provider"].map((provider) => provider.value);
    let updatedValue = { ...values, ...{ providerId } };
    delete updatedValue["provider"];
    return updatedValue;
  };
  const generateReport = (values) => {
    generateInvoice({
      ...values,
    })
      .then((response) => {
        if (
          !_.isEmpty(_.get(response, "errorMessage", "")) ||
          _.get(response, "success", true) === false
        ) {
          _.set(response, "status", 200);

          setApiErrorModalState({ error: response });
        } else {
          downloadCsv(response.fileData, response.fileName);
        }
      })
      .catch((err) => {
        setApiErrorModalState({ error: err });
      });
  };  

  const handleReportSubmit = (values) => {
    if (values?.btnType === "view") {
      delete values["btnType"];
      let formattedValue = getApiFormattedValue(values);
      fetchViewReport(formattedValue, defaultAPIAdditionalParams);
    } else if (values?.btnType === "generate") {
      delete values["btnType"];
      let formattedValue = getApiFormattedValue(values);
      generateReport(formattedValue);
    }
  };


  const hasErrorCheck = (touched, errors, name) => {
    return !!(touched[name] && errors[name]);
  };

  const getUpdatedDropdownValue = (values, value, fieldName, responseValue) => {
    let keyArrayItems = value?.map((item) => item.key);
    if (
      _.includes(keyArrayItems, "Select All") &&
      values[fieldName].length === responseValue.length
    ) {
      value = [];
    } else if (
      _.includes(keyArrayItems, "Select All") &&
      values[fieldName].length !== responseValue.length
    ) {
      value = responseValue;
    }
    return value;
  };

  return (
    <Box className={styles.reportWrapper}>
      <Box className={styles.invoiceTextWrapper}>
        {i18next.t("Invoicing reports")}
      </Box>
      <Box className={styles.whiteBoxWrapper}>
        <Formik
          initialValues={initalValue}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={(values) => {
            handleReportSubmit(values);
          }}
        >
          {({
            values,
            errors,
            touched,
            setFieldTouched,
            setFieldValue,
            handleSubmit,
          }) => (
            <Box className={styles.contentBoxWrapper} data-testid="Report">
              <Box className={styles.contentWrapper}>
                <span className={styles.textContent}></span>

                <Form onSubmit={handleSubmit}>
                  <Box className={styles.textBoxContentWrapper}>
                    <Box className={styles.textBoxWrapper}>
                      <FormikControl
                        variant="filled"
                        control="multiselect"
                        label="Providers"
                        options={providerList}
                        hiddenLabel
                        id="multiSelectDropdown"
                        name="provider"
                        size="small"
                        noOptionsText={
                          <Box>
                            <Box>{i18next.t("No results found")}</Box>
                          </Box>
                        }
                        isSelect={true}
                        disabled={false}
                        placeholder={i18next.t("Select Provider(s)")}
                        className="input-grp__box select"
                        freeSolo={false}
                        hasSubText={false}
                        subTextField={{
                          title: "key",
                        }}
                        onChange={(e, value) => {
                          setFieldTouched("provider", false);
                          let updatedValue = getUpdatedDropdownValue(
                            values,
                            value,
                            "provider",
                            providerList
                          );
                          setFieldValue("provider", updatedValue);
                        }}
                        value={values.provider}
                        hasError={hasErrorCheck(touched, errors, "provider")}
                        error={hasErrorCheck(touched, errors, "provider")}
                        errorMessage={errors?.provider}
                      />
                    </Box>
                    <Box className={styles.textBoxWrapper}>
                      <FormikControl
                        variant="filled"
                        control="datepicker"
                        hiddenLabel
                        label={i18next.t("From date")}
                        id="fromeDate"
                        name="fromeDate"
                        value={values.fromeDate}
                        placeholderText={i18next.t("Select From date")}
                        format={"DD-MM-YYYY"}
                        onChange={(date, { customInput, isTyping }) => {
                          let input = document.getElementById(
                            "fromeDate-datepicker"
                          );

                          setFieldTouched("fromeDate", false);

                          if (!isTyping) {
                            if (_.isEmpty(date?.format())) {
                              return setFieldValue("fromeDate", "");
                            }

                            return setFieldValue("fromeDate", date.format()); // user selects the date from the calendar and no needs for validation.
                          }

                          if (!dateInputValidation(input, date)) {
                            return false;
                          }

                          setFieldValue("fromeDate", date?.format());
                        }}
                        dateFormat={dateFormats.datePickerFormat}
                        error={hasErrorCheck(touched, errors, "fromeDate")}
                        errorMessage={errors?.fromeDate}
                      />
                      <FormikControl
                        variant="filled"
                        control="datepicker"
                        hiddenLabel
                        label={i18next.t("To (inclusive; at most day before yesterday)")}
                        id="toDate"
                        name="toDate"
                        value={values.toDate}
                        placeholderText={i18next.t("Select To date")}
                        format={"DD-MM-YYYY"}
                        onChange={(date, { customInput, isTyping }) => {
                          let input =
                            document.getElementById("toDate-datepicker");

                          setFieldTouched("toDate", false);

                          if (!isTyping) {
                            if (_.isEmpty(date?.format())) {
                              return setFieldValue("toDate", "");
                            }

                            return setFieldValue("toDate", date.format()); // user selects the date from the calendar and no needs for validation.
                          }
                          if (!dateInputValidation(input, date)) {
                            return false;
                          }

                          setFieldValue("toDate", date?.format());
                        }}
                        dateFormat={dateFormats.datePickerFormat}
                        error={hasErrorCheck(touched, errors, "toDate")}
                        errorMessage={errors?.toDate}
                      />
                    </Box>
                  </Box>
                </Form>
              </Box>
              <Box className={styles.buttonWrapper}>
                <Button
                  title={i18next.t("View report")}
                  iconStyle={""}
                  buttonType={"muibtn-secondary-with-border-large-text"}
                  attriType={"button"}
                  withIcon={false}
                  showCountSection={false}
                  count={0}
                  handleClick={() => {
                    setFieldValue("btnType", "view");
                    handleSubmit();
                  }}
                  isDisabled={false}
                />
                <Button
                  title={i18next.t("Generate report")}
                  iconStyle={""}
                  buttonType={"muibtn-primary-with-text-length"}
                  attriType={"submit"}
                  withIcon={false}
                  showCountSection={false}
                  count={0}
                  handleClick={() => {
                    setFieldValue("btnType", "generate");
                    handleSubmit();
                  }}
                  isDisabled={false}
                />

                <Button
                  title={i18next.t("Clear")}
                  iconStyle={""}
                  buttonType={"muibtn-secondary-padding-adjust"}
                  attriType={"submit"}
                  withIcon={false}
                  showCountSection={false}
                  count={0}
                  handleClick={() =>
                    handleClear(setFieldValue, setFieldTouched)
                  }
                  isDisabled={false}
                />
              </Box>
            </Box>
          )}
        </Formik>
      </Box>
      <ReportTabel
        reportTable={reportTable}
        setReportTable={setReportTable}
        onSortChange={(sortingParam) => handleTableSorting(sortingParam)}
        sortModel={sortModel}
        pageSize={_.get(
          reportResponse,
          "paginationResult.pageSize",
          defaultPageSize
        )}
        pageNumber={_.get(
          reportResponse,
          "paginationResult.currentPage",
          defaultPageNumber
        )}
        handlePageChange={(pageNo) =>
          setDefaultAPIAdditionalParams({
            ...defaultAPIAdditionalParams,
            pageNumber: pageNo,
          })
        }
        totalRecords={_.get(reportResponse, "paginationResult.totalCount", 0)}
      />
    </Box>
  );
};

export default Report;
